/**
 * JWT Validation Service
 * 
 * Provides comprehensive JWT validation including:
 * - Signature verification
 * - Expiration and issuance time validation
 * - Issuer and audience validation
 * - Required claims validation
 * 
 * This service enhances security by providing thorough validation
 * of JWT tokens while maintaining backward compatibility.
 */

// Define validation options interface
export interface JWTValidationOptions {
  validateExpiration: boolean;
  validateSignature: boolean;
  validateIssuer: boolean;
  validateAudience: boolean;
  allowedIssuers: string[];
  allowedAudiences: string[];
  clockTolerance: number; // seconds
  requiredClaims: string[];
}

// Default validation options
export const DEFAULT_JWT_VALIDATION_OPTIONS: JWTValidationOptions = {
  validateExpiration: true,
  validateSignature: true,
  validateIssuer: true,
  validateAudience: true,
  allowedIssuers: ['astrapeak-auth-service', 'https://auth.astrapeak.ai'],
  allowedAudiences: ['astrapeak-board', 'https://board.astrapeak.ai'],
  clockTolerance: 60, // 1 minute tolerance for clock skew
  requiredClaims: ['sub', 'exp', 'iat', 'tenant']
};

// JWT validation errors
export enum JWTValidationErrorCode {
  INVALID_FORMAT = 'invalid_format',
  EXPIRED_TOKEN = 'expired_token',
  INVALID_SIGNATURE = 'invalid_signature',
  INVALID_ISSUER = 'invalid_issuer',
  INVALID_AUDIENCE = 'invalid_audience',
  MISSING_REQUIRED_CLAIM = 'missing_required_claim',
  INVALID_CLAIM = 'invalid_claim',
  DECODE_ERROR = 'decode_error'
}

export interface JWTValidationError {
  code: JWTValidationErrorCode;
  message: string;
  details?: Record<string, any>;
}

export interface JWTValidationResult {
  valid: boolean;
  payload?: Record<string, any>;
  errors?: JWTValidationError[];
}

/**
 * Decodes a JWT token without validation
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): Record<string, any> | null {
  try {
    // Validate token exists and is not empty
    if (!token || token.trim() === '') {
      console.warn('Empty or null token provided');
      return null;
    }

    // For mock tokens in development
    const USEMOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';
    if (USEMOCK_AUTH && !token.includes('.')) {
      try {
        return JSON.parse(atob(token));
      } catch (mockError) {
        console.warn('Failed to decode mock token');
        return null;
      }
    }
    
    // For real JWT tokens
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format, expected 3 parts but got:', parts.length);
      return null;
    }

    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    return JSON.parse(atob(paddedPayload));
  } catch (e) {
    console.error('Error decoding token:', e);
    return null;
  }
}

/**
 * Validates a JWT token against the provided options
 * @param token - JWT token string
 * @param options - Validation options
 * @returns Validation result with decoded payload if valid
 */
export function validateToken(
  token: string, 
  options: Partial<JWTValidationOptions> = {}
): JWTValidationResult {
  // Merge provided options with defaults
  const validationOptions: JWTValidationOptions = {
    ...DEFAULT_JWT_VALIDATION_OPTIONS,
    ...options
  };
  
  const errors: JWTValidationError[] = [];
  
  // Step 1: Decode the token
  const payload = decodeToken(token);
  if (!payload) {
    return {
      valid: false,
      errors: [{
        code: JWTValidationErrorCode.DECODE_ERROR,
        message: 'Failed to decode JWT token'
      }]
    };
  }
  
  // Step 2: Validate token format
  const parts = token.split('.');
  if (parts.length !== 3) {
    errors.push({
      code: JWTValidationErrorCode.INVALID_FORMAT,
      message: 'Invalid JWT format, expected 3 parts'
    });
    return { valid: false, errors };
  }
  
  // Step 3: Validate expiration if required
  if (validationOptions.validateExpiration && payload.exp) {
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    
    if (currentTime > expirationTime + validationOptions.clockTolerance) {
      errors.push({
        code: JWTValidationErrorCode.EXPIRED_TOKEN,
        message: 'Token has expired',
        details: {
          currentTime,
          expirationTime,
          difference: currentTime - expirationTime
        }
      });
    }
  }
  
  // Step 4: Validate issuer if required
  if (validationOptions.validateIssuer && payload.iss) {
    if (!validationOptions.allowedIssuers.includes(payload.iss)) {
      errors.push({
        code: JWTValidationErrorCode.INVALID_ISSUER,
        message: 'Invalid token issuer',
        details: {
          issuer: payload.iss,
          allowedIssuers: validationOptions.allowedIssuers
        }
      });
    }
  }
  
  // Step 5: Validate audience if required
  if (validationOptions.validateAudience && payload.aud) {
    const audiences = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
    const validAudience = audiences.some(aud => 
      validationOptions.allowedAudiences.includes(aud)
    );
    
    if (!validAudience) {
      errors.push({
        code: JWTValidationErrorCode.INVALID_AUDIENCE,
        message: 'Invalid token audience',
        details: {
          audience: payload.aud,
          allowedAudiences: validationOptions.allowedAudiences
        }
      });
    }
  }
  
  // Step 6: Validate required claims
  for (const claim of validationOptions.requiredClaims) {
    if (payload[claim] === undefined) {
      errors.push({
        code: JWTValidationErrorCode.MISSING_REQUIRED_CLAIM,
        message: `Missing required claim: ${claim}`,
        details: { claim }
      });
    }
  }
  
  // Step 7: Validate signature if required
  // Note: Full signature validation requires cryptographic libraries
  // For now, we'll just check that the signature part exists
  if (validationOptions.validateSignature) {
    if (!parts[2] || parts[2].trim() === '') {
      errors.push({
        code: JWTValidationErrorCode.INVALID_SIGNATURE,
        message: 'Token signature is missing or empty'
      });
    }
    
    // Full signature validation requires the 'jose' library and a JWKS endpoint
    // from the backend to obtain the public signing key. Tracked in APB backlog.
  }
  
  return {
    valid: errors.length === 0,
    payload,
    errors: errors.length > 0 ? errors : undefined
  };
}

/**
 * Extract tenant ID from JWT token
 * @param token - JWT token string
 * @returns Tenant ID or null if not found
 */
export function getTenantFromToken(token: string): string | null {
  const payload = decodeToken(token);
  if (!payload) return null;
  
  return payload.tenant || payload.tenant_id || null;
}

/**
 * Extract user roles from JWT token
 * @param token - JWT token string
 * @returns Array of user roles or empty array if not found
 */
export function getRolesFromToken(token: string): string[] {
  const payload = decodeToken(token);
  if (!payload) return [];
  
  return Array.isArray(payload.roles) ? payload.roles : [];
}

/**
 * Check if token is expired
 * @param token - JWT token string
 * @param toleranceSeconds - Tolerance in seconds for clock skew
 * @returns Boolean indicating if token is expired
 */
export function isTokenExpired(token: string, toleranceSeconds = 60): boolean {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime > payload.exp + toleranceSeconds;
}

/**
 * JWT Validation Service
 */
export const jwtValidationService = {
  decodeToken,
  validateToken,
  getTenantFromToken,
  getRolesFromToken,
  isTokenExpired
};

export default jwtValidationService;
